import type { Vendor } from './api'
import type { Clock } from './auditFilter'

// What the Evidence screen may show: whose pack is on it, whether the verdict
// on the card is the last check's, and whether the head hash on the clipboard
// is the one on the card.
//
// # WHY THIS IS NOT IN THE SCREEN
//
// Two reviews of this screen (11 October 2026) found ways it could vouch for
// the wrong thing. A pack still building when the scope was changed landed
// under the new scope with Download enabled, and the summary named no vendor,
// so one vendor's pack could be sent as the whole tenant's. "Copied" stayed
// lit after the chain was checked again and its head had moved on. "Chain
// intact" and its tick stayed up while the next check was out, and a vendor's
// name typed but not chosen was built as every vendor. Each is the same
// mistake: an answer shown without what it was an answer to. So each answer
// here carries its question, what is kept and what is dropped is plain code
// with no component in it, and a test can play the sequence that went wrong
// with an API that answers when the test says.

export const EVERY_VENDOR = 'Every vendor in this tenant'

// What a pack was asked for, in the words the summary prints. Taken when the
// request is made and kept with the answer, never read back off the picker,
// which may hold something else by the time the answer lands.
export type PackScope = { vendorId: string; name: string; code: string }

export function packScope(vendorId: string, vendors: Vendor[] | null): PackScope {
  if (vendorId === '') return { vendorId: '', name: EVERY_VENDOR, code: '' }
  const v = vendors?.find((x) => x.id === vendorId)
  // A vendor the list does not hold cannot be picked, so this is the id of
  // one read before the list changed. It is still named by what is known.
  return v ? { vendorId, name: v.displayName || v.legalName, code: v.vendorCode } : { vendorId, name: 'One vendor', code: vendorId }
}

// The part of a pack that says what the API was asked for.
type Asked = { request?: { vendorId?: string } }

export type PackState<P, E> = {
  // What the picker holds: a vendor's id, or empty for every vendor.
  scope: string
  // Letters in the picker's box that have not been chosen from its list. The
  // scope is still what it was, so a build now would be for more (every
  // vendor) than the box appears to say. See buildHeldBy.
  typed: string
  // Build was pressed with such letters in the box, and was not sent.
  held: boolean
  // The one build in flight, and the scope it was asked for. slow once it has
  // been out long enough that the person is offered a way to stop waiting.
  asking: { token: number; scope: PackScope; slow: boolean } | null
  // recorded is whether the API appended the export to the chain when it
  // built this pack. It answers with the pack either way (see exportRecorded
  // in lib/api.ts), so a pack can be on the screen with no act in the audit
  // trail behind it, and the screen has to say which this one is.
  built: { pack: P; scope: PackScope; recorded: boolean } | null
  error: E | null
  // The person stopped waiting for this pack. The request was not called
  // back: there is no calling it back, and the API may still build the pack
  // and record the export. The screen says "may" only for as long as no
  // answer has come. When one does it is kept here, and the screen says what
  // it said (stoppedWords).
  stopped: { scope: PackScope; token: number; answered: { recorded: boolean } | null } | null
  // Packs that came back with nothing on the screen waiting for them and no
  // stopped build to account for them, and were not shown. Each was still
  // built, and each answer said whether its export was recorded. They are
  // counted both ways, so that the note claims no act the audit trail does
  // not hold and passes over none that it does (unshownWords).
  unshown: { recorded: number; unrecorded: number } | null
  // The API answered with a pack for a scope other than the one asked for.
  otherScope: { recorded: boolean } | null
}

export type PackEvent<P, E> =
  // The screen has been opened, for the first time or again.
  | { type: 'opened' }
  | { type: 'scope'; vendorId: string }
  | { type: 'typed'; text: string }
  | { type: 'held' }
  | { type: 'build'; token: number; scope: PackScope }
  // The build has been out long enough to say so, and the person gave up on it.
  | { type: 'slow'; token: number }
  | { type: 'stopped' }
  | { type: 'answer'; token: number; pack: P; recorded: boolean }
  | { type: 'refused'; token: number; error: E }

export function noPack<P, E>(): PackState<P, E> {
  return { scope: '', typed: '', held: false, asking: null, built: null, error: null, stopped: null, unshown: null, otherScope: null }
}

// Why Build would not be sent now, or nothing if it would.
//
// 'building': one is already out. A second would be a second export on the
// chain for one press too many.
//
// 'unchosen': the box holds letters nobody chose a vendor for. The request
// would be for the scope in force, every vendor, and the person pressing the
// button is looking at a vendor's name.
export function buildHeldBy<P, E>(s: PackState<P, E>): 'building' | 'unchosen' | null {
  if (s.asking !== null) return 'building'
  return s.typed.trim() === '' ? null : 'unchosen'
}

// What stands in the picker's place while a pack is building, or nothing.
//
// # WHY THE SCOPE IS LOCKED THEN
//
// For one commit a change of scope dropped the build in flight, and said it
// "has been dropped". It had not been: the request had reached the API, which
// built the pack and appended "evidence pack exported" to the chain, for a
// pack nobody received (the re-review of 11 October 2026 watched the sequence
// go from 23 to 25 that way). An export this screen starts is an export this
// screen shows. So while one is out the scope cannot be changed, and these are
// the words where the picker was.
export function lockedWords<P, E>(s: PackState<P, E>): string {
  if (!s.asking) return ''
  // A request has no time limit, and a locked picker with no way out but a
  // reload is a screen that has stopped. Past the wait it says so, and the
  // row offers Stop waiting.
  return 'Building the pack for ' + whose(s.asking.scope) + (s.asking.slow ? '. It is taking longer than usual.' : '')
}

// What became of the export of a pack that is not on the screen.
const RECORDED = 'The export was recorded in the audit trail.'
const NOT_RECORDED = 'The export could not be recorded in the audit trail.'

// The note for a build the person stopped waiting for.
export function stoppedWords(stopped: NonNullable<PackState<unknown, unknown>['stopped']>): string {
  const lead = 'Stopped waiting for the pack for ' + whose(stopped.scope) + '. '
  if (!stopped.answered) return lead + 'The export may still be recorded in the audit trail.'
  return lead + 'It has since been built, and is not shown. ' + (stopped.answered.recorded ? RECORDED : NOT_RECORDED)
}

// The note for packs that came back with nothing waiting for them.
export function unshownWords(n: { recorded: number; unrecorded: number }): { title: string; body: string } {
  const packs = n.recorded + n.unrecorded
  if (packs === 1) {
    return {
      title: 'A pack came back that nothing on this screen was waiting for',
      body: 'It is not shown or offered for download. ' + (n.recorded === 1 ? RECORDED : NOT_RECORDED),
    }
  }
  let exports = n.recorded + ' of the exports ' + (n.recorded === 1 ? 'was' : 'were') + ' recorded in the audit trail, and ' + n.unrecorded + ' could not be.'
  if (n.unrecorded === 0) exports = 'Each export was recorded in the audit trail.'
  if (n.recorded === 0) exports = 'None of the exports could be recorded in the audit trail.'
  return {
    title: packs + ' packs came back that nothing on this screen was waiting for',
    body: 'They are not shown or offered for download. ' + exports,
  }
}

// The note for a pack that came back for another scope than was asked for.
export function otherScopeWords(other: { recorded: boolean }): string {
  return 'It is not shown or offered for download. ' + (other.recorded ? RECORDED : NOT_RECORDED) + ' Build it again.'
}

// A scope in the middle of a sentence.
export function whose(scope: PackScope): string {
  return scope.vendorId === '' ? 'every vendor in this tenant' : scope.name + ' ' + scope.code
}

// How long a build is out before the screen offers a way to stop waiting.
export const SLOW_AFTER_MS = 30_000

export function packReducer<P extends Asked, E>(s: PackState<P, E>, e: PackEvent<P, E>): PackState<P, E> {
  switch (e.type) {
    case 'opened':
      // What is kept from the last visit is what the person can see on coming
      // back: the scope chosen, a pack building or built, what became of the
      // last build. Letters typed and not chosen are not that. The picker's
      // box starts each visit empty, so letters kept here would hold Build
      // ("choose a vendor, or clear the box") over a box with nothing in it
      // to clear (the final re-review, 11 October 2026).
      return s.typed === '' && !s.held ? s : { ...s, typed: '', held: false }
    case 'scope':
      // Not while a pack is building: see lockedWords. The picker is locked
      // then, and this is what holds if something changes the scope anyway.
      if (s.asking !== null) return s
      // A pick is a pick, and whatever letters there were are not unchosen
      // any more, even when what was picked is what was already chosen
      // (every vendor, picked over letters typed for one).
      if (e.vendorId === s.scope) return s.typed === '' && !s.held ? s : { ...s, typed: '', held: false }
      // Everything on the screen was for the old scope: the pack, a refusal.
      // Not the notes that a pack was built and not shown, or not waited
      // for. Those are about the chain, not about a scope, and they stay
      // until the next build is asked for: a note gone at the next touch of
      // the picker is a note nobody was sure they saw.
      return { ...noPack(), scope: e.vendorId, stopped: s.stopped, unshown: s.unshown, otherScope: s.otherScope }
    case 'typed':
      if (s.asking !== null || e.text === s.typed) return s
      return { ...s, typed: e.text, held: false }
    case 'held':
      return { ...s, held: true }
    case 'build':
      return { ...noPack(), scope: s.scope, typed: s.typed, asking: { token: e.token, scope: e.scope, slow: false } }
    case 'slow':
      return s.asking?.token === e.token && !s.asking.slow ? { ...s, asking: { ...s.asking, slow: true } } : s
    case 'stopped':
      // Unlocked. The answer, if it comes, finds nothing waiting for it. It is
      // not shown, and what it says about the export is kept with the stop.
      return s.asking ? { ...s, asking: null, stopped: { scope: s.asking.scope, token: s.asking.token, answered: null } } : s
    case 'answer':
      // The second line of defence. With the scope locked no build is
      // abandoned from this screen, so an answer nothing is waiting for
      // should not arrive; one that does is not shown, and is not passed over
      // in silence either.
      if (s.asking?.token !== e.token) {
        // The build the person stopped waiting for has answered: "may still
        // be recorded" gives way to what the answer says.
        if (s.stopped?.token === e.token) return { ...s, stopped: { ...s.stopped, answered: { recorded: e.recorded } } }
        const before = s.unshown ?? { recorded: 0, unrecorded: 0 }
        return {
          ...s,
          unshown: e.recorded ? { ...before, recorded: before.recorded + 1 } : { ...before, unrecorded: before.unrecorded + 1 },
        }
      }
      // The pack says what it was asked for. One that names another scope is
      // not shown as this one, whatever the reason it came back.
      if ((e.pack.request?.vendorId ?? '').toLowerCase() !== s.asking.scope.vendorId.toLowerCase()) {
        return { ...s, asking: null, otherScope: { recorded: e.recorded } }
      }
      return { ...s, asking: null, built: { pack: e.pack, scope: s.asking.scope, recorded: e.recorded } }
    case 'refused':
      if (s.asking?.token !== e.token) return s
      return { ...s, asking: null, error: e.error }
  }
}

// The file a pack is saved as. The scope is in the name because the file is
// what gets forwarded, and "evidence-pack-3fb418a19b8f.json" says nothing
// about whose it is to the person it is forwarded to.
export function packFileName(scope: PackScope, contentSha256: string | undefined): string {
  const whose = scope.vendorId === '' ? 'every-vendor' : scope.code.replace(/[^A-Za-z0-9._-]+/g, '-')
  return 'evidence-pack-' + whose + '-' + (contentSha256 ?? 'export').slice(0, 12) + '.json'
}

// A pack as the file it is saved as: the name, and the pack as it arrived,
// formatted and nothing else.
export type PackFile = { name: string; text: string }

export function packFile<P extends { contentSha256?: string }>(built: { pack: P; scope: PackScope }): PackFile {
  return { name: packFileName(built.scope, built.pack.contentSha256), text: JSON.stringify(built.pack, null, 2) }
}

// Hands a file to the browser to save. The page and the URL maker are passed
// in so that a test can stand in for them and see the name the file leaves
// under.
export function saveFile(
  file: PackFile,
  page: { createElement: (tag: 'a') => { href: string; download: string; click: () => void } } = document,
  urls: { createObjectURL: (blob: Blob) => string; revokeObjectURL: (url: string) => void } = URL,
): void {
  const url = urls.createObjectURL(new Blob([file.text], { type: 'application/json' }))
  const a = page.createElement('a')
  a.href = url
  a.download = file.name
  a.click()
  urls.revokeObjectURL(url)
}

// The copy button's word. What was copied is kept with whether it worked, so
// the word is about the hash on the card only when that is the hash that was
// copied. A head that moves on a second check reads Copy again.
export type CopyTry = { hash: string; ok: boolean } | null

export function copyWord(tried: CopyTry, hash: string): 'Copy' | 'Copied' | 'Not copied' {
  if (!tried || tried.hash !== hash) return 'Copy'
  return tried.ok ? 'Copied' : 'Not copied'
}

// ------------------------------------------------------------ the chain check

export type ChainResult = {
  tenantId: string
  // How many events the check read. On a chain that breaks part way this is
  // how far it got, not how many the chain holds.
  events: number
  ok: boolean
  // The sequence of the event the chain breaks at. Absent when every event
  // links and it is the head that does not match (a tail cut off, or a head
  // never written), so there is no one event to name.
  breakAt?: number
  detail?: string
  headHash?: string
}

// The verdict on the card is the verdict of the last check asked for, or
// nothing. While a check is out the old verdict is not on the screen: "Chain
// intact" beside a button that says Checking is the last check's answer drawn
// as if it were this one's, on the screen a person shows an auditor.
export type ChainState = {
  // The check in flight, or null.
  checking: number | null
  chain: ChainResult | null
  error: unknown
}

export type ChainEvent =
  | { type: 'check'; token: number }
  | { type: 'verdict'; token: number; chain: ChainResult }
  | { type: 'failed'; token: number; error: unknown }

export const NO_CHAIN: ChainState = { checking: null, chain: null, error: null }

export function chainReducer(s: ChainState, e: ChainEvent): ChainState {
  switch (e.type) {
    case 'check':
      return { checking: e.token, chain: null, error: null }
    case 'verdict':
      return s.checking === e.token ? { checking: null, chain: e.chain, error: null } : s
    case 'failed':
      // No verdict is left standing under a check that could not run.
      return s.checking === e.token ? { checking: null, chain: null, error: e.error } : s
  }
}

// ----------------------------------------------------------------- the screen

export type EvidenceState<P> = {
  chain: ChainState
  packs: PackState<P, unknown>
  // The register the picker chooses from. null while it is being read; the
  // picker offers the every-vendor choice on its own until it arrives.
  vendors: Vendor[] | null
  // The register could not be read ('failed'), or is being read again after
  // a read that failed ('reading'). The picker says which.
  vendorsUnread: 'failed' | 'reading' | null
}

export function noEvidence<P>(): EvidenceState<P> {
  return { chain: NO_CHAIN, packs: noPack(), vendors: null, vendorsUnread: null }
}

export type EvidenceScreen<P> = {
  state: () => EvidenceState<P>
  // Told on every change. Returns the way to stop being told.
  subscribe: (listener: () => void) => () => void
  // The screen is on the page: check the chain, read the register, and let go
  // of what the last visit left that this one cannot see.
  open: () => void
  check: () => void
  // The picker chose a vendor, or went back to every vendor.
  scope: (vendorId: string) => void
  // The picker's box holds these letters, not yet chosen from its list.
  typed: (text: string) => void
  build: () => void
  stopWaiting: () => void
  // Read the register again, after a read that failed.
  readVendors: () => void
}

const WALL_CLOCK: Clock = {
  set: (fn, ms) => setTimeout(fn, ms),
  clear: (timer) => clearTimeout(timer as ReturnType<typeof setTimeout>),
}

// The screen's requests and what becomes of their answers. Each request
// carries a token and its answer is kept only by the request that is still the
// one waiting, which is all a late answer needs to be harmless.
//
// It outlives the component that draws it. A pack asked for and then walked
// away from is still built and still recorded on the chain; kept here, it is
// on the screen when the person comes back, where one made per visit would
// have let it land on nothing. What it keeps across a visit is only what the
// person sees on coming back: see 'opened' in packReducer.
//
// The register is read here and not by the component, so the pack is named
// from the same list the picker chose from with nothing passed between them.
export function createEvidence<P extends Asked>(
  api: {
    verify: () => Promise<ChainResult>
    // The pack, and whether the API recorded that it built it.
    pack: (params: { vendorId?: string }) => Promise<{ pack: P; recorded: boolean }>
    vendors: () => Promise<Vendor[]>
  },
  clock: Clock = WALL_CLOCK,
): EvidenceScreen<P> {
  let state = noEvidence<P>()
  let checks = 0
  let builds = 0
  let reads = 0
  let slowTimer: unknown = null
  const listeners = new Set<() => void>()
  const put = (next: EvidenceState<P>) => {
    state = next
    for (const told of listeners) told()
  }
  const chain = (e: ChainEvent) => put({ ...state, chain: chainReducer(state.chain, e) })
  const packs = (e: PackEvent<P, unknown>) => {
    const next = packReducer(state.packs, e)
    // The wait is for the build that is out. Once nothing is out, nothing is
    // waited for.
    if (next.asking === null && slowTimer !== null) {
      clock.clear(slowTimer)
      slowTimer = null
    }
    if (next !== state.packs) put({ ...state, packs: next })
  }
  const readVendors = () => {
    const mine = ++reads
    // A failure on the screen stops being the last word the moment another
    // read is out, and the press that asked for it is seen to have landed.
    if (state.vendorsUnread !== null) put({ ...state, vendorsUnread: 'reading' })
    api.vendors().then(
      (vendors) => {
        if (mine === reads) put({ ...state, vendors, vendorsUnread: null })
      },
      () => {
        // The list from an earlier visit, if there is one, is kept: the vendor
        // chosen from it is still named. It is not made an empty list, which
        // would read as a tenant with no vendors.
        if (mine === reads) put({ ...state, vendorsUnread: 'failed' })
      },
    )
  }
  const check = () => {
    const token = ++checks
    chain({ type: 'check', token })
    api.verify().then(
      (result) => chain({ type: 'verdict', token, chain: result }),
      (error) => chain({ type: 'failed', token, error }),
    )
  }
  return {
    state: () => state,
    subscribe(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    open() {
      packs({ type: 'opened' })
      check()
      readVendors()
    },
    readVendors,
    check,
    scope: (vendorId) => packs({ type: 'scope', vendorId }),
    typed: (text) => packs({ type: 'typed', text }),
    build() {
      const held = buildHeldBy(state.packs)
      // A second press while one is out asks for nothing and says nothing new:
      // the picker's place already says a pack is being built.
      if (held === 'building') return
      if (held === 'unchosen') return packs({ type: 'held' })
      const token = ++builds
      // Named now, from what the picker holds as the request leaves.
      const scope = packScope(state.packs.scope, state.vendors)
      packs({ type: 'build', token, scope })
      slowTimer = clock.set(() => {
        slowTimer = null
        packs({ type: 'slow', token })
      }, SLOW_AFTER_MS)
      api.pack(scope.vendorId ? { vendorId: scope.vendorId } : {}).then(
        (answer) => packs({ type: 'answer', token, pack: answer.pack, recorded: answer.recorded }),
        (error) => packs({ type: 'refused', token, error }),
      )
    },
    stopWaiting: () => packs({ type: 'stopped' }),
  }
}
